import { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { leadsApi, type CampaignLeadAnalyticsResponseDto, type CompanySignalDto, type LeadSignalTimelineDto } from '../../api/leads'
import AudienceViewToggle from '@/components/common/AudienceViewToggle'
import { DEFAULT_AUDIENCE_VIEW, emptyStateMessage, filterCompaniesByAudience, filterLeadsByAudience, type AudienceView } from '@/utils/leadAudienceFilter'
import { SignalBars } from '@/components/common/SignalBars'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'
import { useEnrichmentEvent } from '@/hooks/useEnrichmentEvent'
import { CompanyNameCell } from '@/components/companies/CompanyNameCell'
import { LeadNameCell } from '@/components/leads/LeadNameCell'
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell'
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell'
import { useUserProfile } from '@/hooks/useUserProfile'
import { getWebTypeMeta, type WebTypeKey } from '../../constants/webTypes'
import { timeFromNowVerbose } from '@/utils/time'
import { RefreshCcw } from 'lucide-preact'
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting'
import { LoadingRow, ErrorState, EmptyState } from '@/components/common/TableFragments'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import { useCompany } from '@/hooks/useCompany'
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport'

interface Props {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
}

type LeadsMode = 'contacts' | 'companies'

function useLeadsMode(): [LeadsMode, (m: LeadsMode) => void] {
  const initial: LeadsMode = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('mode')
      return p === 'companies' ? 'companies' : 'contacts'
    } catch {
      return 'contacts'
    }
  })()
  const [mode, setMode] = useState<LeadsMode>(initial)
  const update = (next: LeadsMode) => {
    setMode(next)
    try {
      const url = new URL(window.location.href)
      if (next === 'contacts') url.searchParams.delete('mode')
      else url.searchParams.set('mode', next)
      window.history.replaceState({}, '', url.toString())
    } catch {/* ignore */ }
  }
  return [mode, update]
}

export const CampaignLeadsTab: FunctionalComponent<Props> = ({ clientCompanyId, workspaceId, campaignId }) => {
  const { company, currentWorkspace } = useCompany()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
  const [data, setData] = useState<CampaignLeadAnalyticsResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useLeadsMode()
  const [audienceView, setAudienceView] = useState<AudienceView>(DEFAULT_AUDIENCE_VIEW)
  const [showNonProspects, setShowNonProspects] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LeadSignalTimelineDto | null>(null)

  // Live-flip the grid's enrichment cell when a lead's enrichment finishes (ORB-383).
  useEnrichmentEvent((evt) => {
    if (!evt.leadId) return
    setData(prev => prev ? { ...prev, Leads: prev.Leads.map(l => l.LeadId === evt.leadId ? { ...l, EnrichmentStatus: evt.enrichmentStatus } : l) } : prev)
  })
  const EMPTY_ANALYTICS: CampaignLeadAnalyticsResponseDto = { CampaignId: campaignId, Leads: [], Companies: [], LeadCount: 0, NonProspectCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() }
  const { updateContext } = useAIChatContextUpdater()

  const load = (signal?: AbortSignal) => {
    setLoading(true)
    setError(null)
    leadsApi.getCampaignLeads(campaignId, workspaceId, signal)
      .then(r => {
        const payload = (r?.data as CampaignLeadAnalyticsResponseDto | undefined)
        if (payload && Array.isArray(payload.Leads)) {
          setData(payload)
        } else {
          setData(EMPTY_ANALYTICS)
        }
      })
      .catch(e => {
        if (e instanceof Error && e.name === 'AbortError') return
        setError(e?.message || 'Unknown error')
        setData(EMPTY_ANALYTICS)
      })
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    const abortController = new AbortController()
    load(abortController.signal)
    return () => abortController.abort()
  }, [campaignId])

  const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—'
  const leads = data?.Leads || []
  const companies = data?.Companies || []
  // Companies follow the same non-prospect switch as Contacts, or the two modes
  // disagree about whether ORTEC and OrbitalX are in this campaign (ORB-619).
  const visibleCompanies = filterCompaniesByAudience(companies, audienceView, showNonProspects)
  const hasOnlineEvent = leads.some(l => l.WebTypes?.includes('Online Event'))
  const filteredLeads = (mode === 'contacts')
    ? filterLeadsByAudience(leads, audienceView, showNonProspects)
    : leads

  // Update AI context when data loads
  useEffect(() => {
    if (!data) return

    updateContext({
      campaignId,
      location: ChatLocations.CampaignLeads,
      entityType: ChatEntityTypes.Campaign,
      viewMode: ChatViewModes.View,
      availableFields: [
        { name: 'totalLeads', displayName: 'Total Leads', fieldType: 'number', currentValue: data.LeadCount.toString(), isEditable: false, isFocused: false },
        { name: 'totalCompanies', displayName: 'Total Companies', fieldType: 'number', currentValue: data.CompanyCount.toString(), isEditable: false, isFocused: false },
        { name: 'viewMode', displayName: 'View Mode', fieldType: 'text', currentValue: mode, isEditable: false, isFocused: false },
      ],
      availableActions: [
        'View Lead Details',
        'Export Leads',
        'Toggle Contacts/Companies View',
        'Filter Leads',
      ],
    })
  }, [data, mode, workspaceId, campaignId, updateContext])

  // Deselect lead if it's filtered out
  useEffect(() => {
    if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId)) {
      setSelectedLead(null)
    }
  }, [filteredLeads, selectedLead])

  // Close on ESC
  useEffect(() => {
    if (!selectedLead) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedLead(null) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedLead])

  // Apply partial lead/contact updates from side panel without full reload
  const handleLeadUpdated = (updated: Partial<LeadSignalTimelineDto> & { LeadId: string }) => {
    setData(prev => {
      if (!prev) return prev
      const nextLeads = prev.Leads.map(l => {
        if (l.LeadId !== updated.LeadId) return l
        const merged: LeadSignalTimelineDto = { ...l, ...updated }
        if (updated.FirstName !== undefined || updated.LastName !== undefined) {
          const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ')
          merged.FullName = fn.length > 0 ? fn : merged.FullName
        }
        return merged
      })
      return { ...prev, Leads: nextLeads }
    })
    setSelectedLead(curr => {
      if (!curr || curr.LeadId !== updated.LeadId) return curr
      const merged: LeadSignalTimelineDto = { ...curr, ...updated }
      if (updated.FirstName !== undefined || updated.LastName !== undefined) {
        const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ')
        merged.FullName = fn.length > 0 ? fn : merged.FullName
      }
      return merged
    })
  }

  // ---------- Contact columns for DataGrid ----------
  const contactColumns = useMemo((): ColumnConfig<LeadSignalTimelineDto>[] => {
    const cols: ColumnConfig<LeadSignalTimelineDto>[] = [
      {
        key: 'lead',
        header: 'Lead',
        sortable: true,
        required: true,
        className: 'whitespace-nowrap',
        render: (l) => <LeadNameCell lead={l} />,
      },
      {
        key: 'phone',
        header: 'Phone',
        sortable: true,
        render: (l, index) => (
          <PhoneEnrichmentCell
            phone={l.Phone}
            globalContactPhone={l.GlobalContactPhone}
            phoneEnrichmentAvailableDate={l.PhoneEnrichmentAvailableDate}
            id={l.LeadId}
            clientCompanyId={clientCompanyId}
            workspaceId={workspaceId}
            campaignId={campaignId}
            canEdit={canEdit}
            isLastRow={index === filteredLeads.length - 1 && filteredLeads.length > 1}
            onPhoneUpdated={(updated) => {
              const finalUpdate = {
                ...updated,
                LeadId: l.LeadId,
                Phone: updated.GlobalContactPhone || updated.Phone
              }
              handleLeadUpdated(finalUpdate)
            }}
          />
        ),
      },
      {
        key: 'enrichment',
        header: 'Enrichment',
        sortable: true,
        render: (l) => (
          <EnrichmentStatusCell
            enrichmentStatus={l.EnrichmentStatus}
            enrichedAt={l.EnrichedAt}
            enrichmentAvailableDate={l.EnrichmentAvailableDate}
            enrichmentAttemptCount={l.EnrichmentAttemptCount}
            email={l.Email}
            clientCompanyId={clientCompanyId}
            workspaceId={workspaceId}
            campaignId={campaignId}
            id={l.LeadId}
            onEnrichmentUpdated={(updated) => handleLeadUpdated({ ...updated, LeadId: l.LeadId })}
          />
        ),
      },
      {
        key: 'company',
        header: 'Company',
        sortable: true,
        className: 'truncate max-w-[160px]',
        render: (l) => <span title={l.Company || '—'}>{l.Company || '—'}</span>,
      },
      {
        key: 'title',
        header: 'Title',
        sortable: true,
        className: 'truncate max-w-[180px]',
        render: (l) => <span title={l.JobTitle || '—'}>{l.JobTitle || '—'}</span>,
      },
      {
        key: 'types',
        header: 'Types',
        render: (l) => (
          <div class="flex flex-wrap gap-1">
            {l.WebTypes?.map(wt => {
              const meta = getWebTypeMeta(wt as WebTypeKey | string)
              return <span key={wt} class={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>{wt}</span>
            })}
          </div>
        ),
      },
    ]

    if (hasOnlineEvent) {
      cols.push({
        key: 'webinarAttended',
        header: 'Webinar Attended',
        sortable: true,
        defaultVisible: false,
        className: 'whitespace-nowrap',
        render: (l) => <>{l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '—'}</>,
      }, {
        key: 'webinarMinutesWatched',
        header: 'Minutes Watched',
        sortable: true,
        defaultVisible: false,
        className: 'whitespace-nowrap',
        render: (l) => <>{l.WebinarMinutesWatched ?? '—'}</>,
      })
    }

    cols.push(
      {
        key: 'lastConversion',
        header: 'Last Interaction',
        sortable: true,
        className: 'whitespace-nowrap',
        render: (l) => <>{l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—'}</>,
      },
      {
        key: 'signal',
        header: 'Campaign Signal',
        sortable: true,
        required: true,
        className: 'whitespace-nowrap',
        render: (l) => <SignalBars score={l.SignalScore} />,
      },
    )

    return cols
  }, [clientCompanyId, workspaceId, campaignId, hasOnlineEvent, filteredLeads.length])

  // ---------- Company columns for DataGrid ----------
  const companyColumns = useMemo((): ColumnConfig<CompanySignalDto>[] => [
    {
      key: 'company',
      header: 'Company',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (c) => (
        <CompanyNameCell company={c} clientCompanyId={clientCompanyId} workspaceId={workspaceId} />
      ),
    },
    {
      key: 'url',
      header: 'URL',
      className: 'truncate max-w-[180px]',
      render: (c) => {
        if (!c.CompanyWebsite) return <>{'—'}</>
        try {
          const host = new URL(c.CompanyWebsite).hostname.replace(/^www\./, '')
          return <a href={c.CompanyWebsite} target="_blank" rel="noopener noreferrer" class="hover:underline" title={c.CompanyWebsite}>{host}</a>
        } catch {
          return <a href={c.CompanyWebsite} target="_blank" rel="noopener noreferrer" class="hover:underline" title={c.CompanyWebsite}>{c.CompanyWebsite}</a>
        }
      },
    },
    {
      key: 'industry',
      header: 'Industry',
      sortable: true,
      className: 'truncate max-w-[160px]',
      render: (c) => <span title={c.Industry || '—'}>{c.Industry || '—'}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      sortable: true,
      render: (c) => <>{c.StaffCountRange || '—'}</>,
    },
    {
      key: 'lastConversion',
      header: 'Last Interaction',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (c) => <>{c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—'}</>,
    },
    {
      key: 'leads',
      header: 'Leads',
      sortable: true,
      render: (c) => <>{c.LeadCount}</>,
    },
    {
      key: 'signal',
      header: 'Campaign Signal',
      sortable: true,
      required: true,
      className: 'whitespace-nowrap',
      render: (c) => <SignalBars score={c.SignalScore} />,
    },
  ], [])

  // ---------- Export configs for DataGrid ----------
  const contactExportConfig = useMemo(() => ({
    getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Campaign', 'leads'),
    columns: LEAD_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
  }), [company?.Name, currentWorkspace?.Name])

  const companyExportConfig = useMemo(() => ({
    getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Campaign', 'companies'),
    columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
  }), [company?.Name, currentWorkspace?.Name])

  // Built once and rendered by every branch below, including the ones that render no grid at all.
  const metricsBar = (
    <MetricsBar
      data={data}
      generatedRelative={generatedRelative}
      mode={mode}
      onModeChange={setMode}
      audienceView={audienceView}
      onAudienceViewChange={setAudienceView}
      showNonProspects={showNonProspects}
      onToggleNonProspects={() => setShowNonProspects(v => !v)}
      onRefresh={() => load()}
    />
  )

  if (loading) return (
    <div class="w-full">
      {metricsBar}
      <table class="min-w-full text-sm"><tbody>{Array.from({ length: 3 }).map((_, i) => <LoadingRow key={i} />)}</tbody></table>
    </div>
  )
  if (error) return (
    <div>{metricsBar}<ErrorState error={error} onRetry={() => load()} /></div>
  )

  const showLeads = mode === 'contacts'
  const showCompanies = mode === 'companies'

  if (showLeads && filteredLeads.length === 0) return (
    <div>{metricsBar}<EmptyState message={emptyStateMessage(audienceView, showNonProspects, leads.length)} /></div>
  )
  if (showCompanies && visibleCompanies.length === 0) return (
    <div>{metricsBar}<EmptyState message="No company signal yet." /></div>
  )

  return (
    <>
      <div class="w-full">
        {metricsBar}
        {showLeads && (
          <DataGrid<LeadSignalTimelineDto>
            title="Contacts"
            data={filteredLeads}
            columns={contactColumns}
            getRowKey={(l) => l.LeadId}
            onRowClick={setSelectedLead}
            getSortComparator={getLeadComparator}
            scrollable
            pagination
            // The audience controls are the only things that narrow this set — there is no search box
            // on this tab — and two of their combinations can leave the same number of rows.
            pageResetKey={`${audienceView}|${showNonProspects}`}
            columnVisibilityKey="orbitalx-lead-columns-campaign"
            exportConfig={contactExportConfig}
          />
        )}
        {showCompanies && (
          <DataGrid<CompanySignalDto>
            title="Companies"
            data={visibleCompanies}
            columns={companyColumns}
            getRowKey={(c) => c.CompanyId || c.CompanyName}
            getSortComparator={getCompanyComparator}
            scrollable
            pagination
            pageResetKey={`${audienceView}|${showNonProspects}`}
            columnVisibilityKey="orbitalx-company-columns-campaign"
            exportConfig={companyExportConfig}
          />
        )}
      </div>
      {selectedLead && (
        <ContactPeekFlyout
          anchorType="lead"
          anchorId={selectedLead.LeadId}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </>
  )
}

interface MetricsBarProps {
  data: CampaignLeadAnalyticsResponseDto | null
  generatedRelative: string
  mode: LeadsMode
  onModeChange: (m: LeadsMode) => void
  audienceView: AudienceView
  onAudienceViewChange: (view: AudienceView) => void
  showNonProspects: boolean
  onToggleNonProspects: () => void
  onRefresh: () => void
}

/**
 * Campaign totals, the audience filters and the Contacts/Companies switch.
 *
 * Module scope, not the tab's body: declared inline it was a new component type on every render, so
 * every filter click remounted the bar and dropped focus from the control that had just been used.
 *
 * These controls stay above the card rather than moving into the grid's toolbar (ORB-715), because the
 * loading, error and empty branches render this bar with no grid at all — a control in the toolbar would
 * disappear exactly when a filter had emptied the list and the reader needed it to get back.
 */
const MetricsBar: FunctionalComponent<MetricsBarProps> = ({
  data,
  generatedRelative,
  mode,
  onModeChange,
  audienceView,
  onAudienceViewChange,
  showNonProspects,
  onToggleNonProspects,
  onRefresh,
}) => (
  <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3">
    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
      {/* Unfiltered campaign totals from the API — not the same number as the grid's own "N results",
          which counts the rows the current filters left on screen. The two disagreeing is the point. */}
      <span><span class="font-semibold text-gray-900 dark:text-gray-100">Leads:</span> {data?.LeadCount ?? '—'}</span>
      <span><span class="font-semibold text-gray-900 dark:text-gray-100">Companies:</span> {data?.CompanyCount ?? '—'}</span>
      <span class="hidden xs:inline"><span class="font-semibold text-gray-900 dark:text-gray-100">Generated:</span> {generatedRelative}</span>
      <AudienceViewToggle
        view={audienceView}
        onChange={onAudienceViewChange}
        showNonProspects={showNonProspects}
        onToggleNonProspects={onToggleNonProspects}
        // Counts what the switch would actually reveal in the mode you're in: hidden
        // leads in Contacts, hidden companies in Companies. They differ a lot — 27
        // internal registrants can sit behind only 3 companies.
        nonProspectCount={mode === 'contacts'
          ? data?.NonProspectCount
          : (data?.Companies ?? []).filter(company => company.IsNonProspect).length}
      />
    </div>
    <div class="flex items-center gap-2">
      <LeadsModeSwitch mode={mode} onChange={onModeChange} />
      <button
        onClick={onRefresh}
        class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        title="Refresh"
      >
        <RefreshCcw class="w-4 h-4" />
        Refresh
      </button>
    </div>
  </div>
)

const LeadsModeSwitch: FunctionalComponent<{ mode: LeadsMode; onChange: (m: LeadsMode) => void }> = ({ mode, onChange }) => {
  const options: Array<{ key: LeadsMode; label: string }> = [
    { key: 'contacts', label: 'Contacts' },
    { key: 'companies', label: 'Companies' }
  ]
  return (
    <div role="radiogroup" aria-label="Leads view mode" class="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
      {options.map(o => {
        const active = o.key === mode
        return (
          <button
            key={o.key}
            role="radio"
            aria-checked={active}
            onClick={() => !active && onChange(o.key)}
            class={`px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10
              ${active
                ? 'bg-green-600 text-white dark:bg-green-500'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}
            `}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
